import React, { useCallback, useContext, useState, useEffect, useMemo, useRef, Fragment, ChangeEvent } from "react";
import { Dropdown } from "react-bootstrap";

import { QuestionOption, QuestionProps } from "./Props";
import { languageContent } from "../Util";
import InterfaceContext from "../Context";
import { useLocalizedStrings } from "../Localization";

import { Explanation } from "./Explanation";
import { SingleSelectQuestion } from "./Choice";
import { safeParse } from "../Util";
import * as v0 from "@aidkitorg/types/lib/survey";
import { fisherYatesShuffle, hash } from "../utils/orderRandomizer";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";

export function LikertQuestion(props: QuestionProps) {
	const context = useContext(InterfaceContext);
	const L = useLocalizedStrings();
	const metadata = safeParse(props.Metadata || '{}') as { questions: v0.Likert['questions'], randomizeOrder: v0.Likert['randomizeOrder'], display: v0.Likert['display']};
	const targetField = props["Target Field"]!;
	let questions = metadata?.questions && Array.isArray(metadata.questions)
		? metadata.questions
		: [];

	let seed: string | null = null;
	if (metadata?.randomizeOrder && targetField) {
		seed = localStorage.getItem('ChoiceRandomizerSeed' + (props.uid || ''));
		if (seed === null) {
			seed = Math.random() + (props.uid || '');
			localStorage.setItem('ChoiceRandomizerSeed' + (props.uid || ''), seed);
		}
		questions = fisherYatesShuffle(questions, hash(0, seed), targetField);
	}

	const options = props["Options (if relevant)"] || [];
	const additionalOptions = props["Additional Options"] || [];
	const setInfoKey = useCallback(props.setInfoKey, [props.info]);

	const [windowWidth, setWindowWidth] = useState(window.innerWidth);

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener("resize", handleResize);

		return () => window.removeEventListener("resize", handleResize);
	}, [windowWidth]);

	function updateValue(targetField: string, newValue: string) {
		if (props.info[targetField] !== newValue) {
			setInfoKey(
				targetField,
				newValue,
				newValue.length > 0 || false,
				false
			);
		}
	}

	async function confirmDelete() {
		if (window.confirm(L.questions.choice.are_you_sure)) {
			// Resets all questions
			for (const question of questions) {
				updateValue(question.targetField, "");
			}
		}
	}

	if ((additionalOptions).indexOf("Hidden") !== -1) {
		return <span></span>
	}

	const formattedContent = useModularMarkdown({
		content: props[languageContent(context.lang)] || '',
		info: props.info,
	});
    const content = additionalOptions.indexOf("Formatted") !== -1
        ? formattedContent
        : <b>{props[languageContent(context.lang)]}</b>;

	const explanationProps = {
		Viewer: props.Viewer,
		Question: props.Question,
		"Field Type": props["Field Type"],
		"English Content": props["English Content"],
		"Spanish Content": props["Spanish Content"],
		setInfoKey: props.setInfoKey,
	}

	const displayMode = () => {
		const isMobile = windowWidth < 600;
		if (metadata.display === 'horizontal') return 'horizontal';
		if (metadata.display === 'responsive_horizontal') return isMobile ? 'stacked' : 'horizontal';
		if (isMobile || options.length > 5) return 'stacked';
		return 'horizontal';
	}

	const showStackedOptions = useMemo(() => displayMode() === "stacked", [windowWidth, metadata.display, options.length]);

	if (showStackedOptions) {
		return (
			<div className="mt-2.5">
				<Explanation {...explanationProps} />
				{questions.map((question, index: number) => {
					const questionProps = {
						"Options (if relevant)": options,
						info: props.info,
						setInfoKey,
						"Target Field": question.targetField,
						"English Content": question.label['en'],
						"Spanish Content": question.label['es'],
					}
					let previousNotAnswered = index > 0 && !props.info[questions[index-1].targetField];
					if (previousNotAnswered && !additionalOptions.includes('Optional')) return null;
					return (
						<div key={question.targetField} className="py-3.5">
							<SingleSelectQuestion {...props} {...question} {...questionProps} />
						</div>
					)
				})}
			</div>
		)
	}

	// Shows Likert scale format with horizontal options
	const showValueScale = (additionalOptions).indexOf("Show Value Scale") !== -1;
	const isVisible = (index: number) => {
		if (!showValueScale) return true;
		return index === 0 || index === options.length - 1;
	};

	return (
		<div className="w-full">
			<fieldset>
				<legend className="text-base text-gray-900">
					{content}
					<Dropdown className="applicant-led-hidden" style={{ display: 'inline' }}>
						<Dropdown.Toggle variant="link">...</Dropdown.Toggle>
						<Dropdown.Menu>
							<Dropdown.Item key={`clear-answers-for-${props[languageContent(context.lang)]}`}
								onClick={() => confirmDelete()}>
								{L.questions.choice.clear_answer}
							</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
				</legend>
			</fieldset>
			<table className="table-auto w-full !mt-5">
				<thead className="sticky top-0 bg-white">
					<tr className="py-4">
						<th className="placeholder w-2/5 min-w-[100px]">{L.questions.x_of_y_completed.replace('$x', (questions || []).reduce((acc, cur) => props.info[cur.targetField] ? acc + 1 : acc, 0) + '').replace('$y', questions.length + '')}</th>
						{options.map((option: any, i: number) =>
							<th key={option.Name}
								className={`max-w-xs px-1 pb-2.5 text-xs font-normal text-center ${!isVisible(i) && ' invisible'}`}>
								{option[languageContent(context.lang).replace('Content', 'Text')] || option[context.lang]}
							</th>
						)}
					</tr>
					{showValueScale && (
						<tr className="py-4">
							<td className="placeholder w-auto max-w-xs"></td>
							{options.map((option: QuestionOption) =>
								<td key={option.Name} className="px-1 pb-2.5 text-xs font-normal text-center">{option.Name}</td>
							)}
						</tr>
					)}
				</thead>
				<tbody>
					{questions.map((q, index) => {
						let previousNotAnswered = index > 0 && !props.info[questions[index-1].targetField];
						if (previousNotAnswered && !additionalOptions.includes('Optional')) return null;
						return <tr key={q.targetField} className="odd:bg-gray-50 rounded-sm">
							<td className="px-3 py-2">{q.label[context.lang]}</td>
							{options.map((option: QuestionOption) =>
								<td key={option.Name} className="text-center">
									<input
										type="radio"
										id={option.Name}
										name={q.targetField}
										value={option.Name}
										onChange={(o) => updateValue(q.targetField, option.Name)}
										checked={props.info[q.targetField] === option.Name} />
								</td>
							)}
						</tr>
					})}
				</tbody>
			</table>
		</div>
	);
}
